import { renderPreMountError } from './preMountErrorView'

// Production browser diagnostics are code-only. React 18 logs captured errors
// BEFORE componentDidCatch; sanitizing only the boundary is too late. Install
// at first bootstrap evaluation, before renderer/auth/application side effects.
// This is not a third-party-script sandbox or an infrastructure log policy.
let installed = false

export function installReleaseDiagnostics(): void {
  if (installed) return
  installed = true
  const native = {
    error: console.error.bind(console), warn: console.warn.bind(console),
    log: console.log.bind(console), info: console.info.bind(console), debug: console.debug.bind(console),
  }
  const emit = (code: string, boundary = false, level: keyof typeof native = 'error') => {
    try {
      native[level](boundary ? 'React Error Boundary caught an error:' : 'VitaHelm diagnostic:', { code })
    } catch { /* A broken diagnostic consumer must not break recovery. */ }
  }
  const boundaryCode = (args: unknown[]): 'RENDER_FAILED' | 'STALE_CHUNK' | null => {
    if (args[0] !== 'React Error Boundary caught an error:') return null
    try {
      if (!args[1] || typeof args[1] !== 'object') return null
      const field = Object.getOwnPropertyDescriptor(args[1], 'code')
      if (field && 'value' in field && (field.value === 'RENDER_FAILED' || field.value === 'STALE_CHUNK')) return field.value
    } catch { /* Never coerce errors or invoke getters/proxy traps to log them. */ }
    return null
  }

  // Free-form diagnostics can contain tokens even when the argument is a plain
  // string (including assert/group/time/count labels), not only an Error. No
  // stack-producing native trace/table/dir implementation receives those args.
  const methods = ['log', 'info', 'debug', 'warn', 'error', 'assert', 'dir', 'dirxml',
    'table', 'trace', 'group', 'groupCollapsed', 'groupEnd', 'count', 'countReset',
    'time', 'timeLog', 'timeEnd', 'profile', 'profileEnd', 'timeStamp', 'clear'] as const
  for (const method of methods) {
    const safe = (...args: unknown[]) => {
      if (method === 'assert' && args[0]) return
      if (['groupEnd', 'clear', 'profileEnd', 'countReset'].includes(method)) return
      if (method !== 'error' && method !== 'assert') return
      const code = method === 'error' ? boundaryCode(args) : null
      emit(code || 'CONSOLE_ERROR', code !== null, 'error')
    }
    // Do not allow a later console-capture integration to receive private raw
    // arguments upstream of this policy. Configure telemetry at safe emitters.
    Object.defineProperty(console, method, { value: safe, writable: false, configurable: false })
  }

  const capture = (event: Event, code: 'UNHANDLED_ERROR' | 'UNHANDLED_REJECTION') => {
    // Prevent the browser's native uncaught diagnostic AND later raw-event
    // reporters. Never access message/error/reason/filename or stringify them.
    event.preventDefault()
    event.stopImmediatePropagation()
    emit(code)
    renderPreMountError({ title: 'STARTUP_FAILED' })
  }
  window.addEventListener('error', event => {
    // Element/resource load failures also reach Window capture listeners as plain
    // Events (not ErrorEvent). Leave those untouched so the browser retains its
    // native resource diagnostic; only runtime exceptions belong to this sink.
    if (!(event instanceof ErrorEvent)) return
    capture(event, 'UNHANDLED_ERROR')
  }, true)
  window.addEventListener('unhandledrejection', event => capture(event, 'UNHANDLED_REJECTION'), true)
}

if (import.meta.env.PROD) installReleaseDiagnostics()
